/** Artboard size baked into early-access-gate-background.html DATA */
export const EARLY_ACCESS_BACKGROUND_WIDTH = 1920;
export const EARLY_ACCESS_BACKGROUND_HEIGHT = 1080;

export const EARLY_ACCESS_SPOTLIGHT_MESSAGE = 'morfi-early-access-spotlight' as const;
export const EARLY_ACCESS_ENTER_MESSAGE = 'morfi-early-access-enter' as const;
export const EARLY_ACCESS_ANIMATION_MESSAGE = 'morfi-early-access-animation' as const;
export const EARLY_ACCESS_PLAYBACK_MESSAGE = 'morfi-early-access-playback' as const;
export const EARLY_ACCESS_READY_MESSAGE = 'morfi-early-access-ready' as const;

export type EarlyAccessSpotlightPayload = {
  type: typeof EARLY_ACCESS_SPOTLIGHT_MESSAGE;
  x: number | null;
  y: number | null;
};

export type EarlyAccessAnimationEvent = 'reveal-start' | 'reveal-complete';

export type EarlyAccessAnimationPayload = {
  type: typeof EARLY_ACCESS_ANIMATION_MESSAGE;
  event: EarlyAccessAnimationEvent;
};

// ALLOW-UNKNOWN: iframe postMessage payloads are untyped at runtime.
export function isEarlyAccessAnimationPayload(data: unknown): data is EarlyAccessAnimationPayload {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const payload = data as EarlyAccessAnimationPayload;
  return (
    payload.type === EARLY_ACCESS_ANIMATION_MESSAGE &&
    (payload.event === 'reveal-start' || payload.event === 'reveal-complete')
  );
}

function coverFitArtboard(
  clientX: number,
  clientY: number,
  viewportWidth: number,
  viewportHeight: number,
  viewportLeft: number,
  viewportTop: number,
): { x: number; y: number } {
  const scale = Math.max(
    viewportWidth / EARLY_ACCESS_BACKGROUND_WIDTH,
    viewportHeight / EARLY_ACCESS_BACKGROUND_HEIGHT,
  );
  const offsetX = viewportLeft + (viewportWidth - EARLY_ACCESS_BACKGROUND_WIDTH * scale) / 2;
  const offsetY = viewportTop + (viewportHeight - EARLY_ACCESS_BACKGROUND_HEIGHT * scale) / 2;

  return {
    x: (clientX - offsetX) / scale,
    y: (clientY - offsetY) / scale,
  };
}

/** Map viewport client coords to the iframe canvas artboard (fullscreen cover fit). */
export function clientToEarlyAccessArtboard(
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  return coverFitArtboard(clientX, clientY, window.innerWidth, window.innerHeight, 0, 0);
}

/** Map client coords using a host rect — matches the iframe's cover fit when embedded. */
export function clientToEarlyAccessArtboardInRect(
  clientX: number,
  clientY: number,
  rect: DOMRectReadOnly,
): { x: number; y: number } {
  return coverFitArtboard(clientX, clientY, rect.width, rect.height, rect.left, rect.top);
}

export function buildEarlyAccessSpotlightPayload(
  clientX: number,
  clientY: number,
  hostRect?: DOMRectReadOnly,
): EarlyAccessSpotlightPayload {
  const { x, y } =
    hostRect === undefined
      ? clientToEarlyAccessArtboard(clientX, clientY)
      : clientToEarlyAccessArtboardInRect(clientX, clientY, hostRect);
  return { type: EARLY_ACCESS_SPOTLIGHT_MESSAGE, x, y };
}

export function buildEarlyAccessSpotlightClearPayload(): EarlyAccessSpotlightPayload {
  return { type: EARLY_ACCESS_SPOTLIGHT_MESSAGE, x: null, y: null };
}

export function buildEarlyAccessEnterPayload(): { type: typeof EARLY_ACCESS_ENTER_MESSAGE } {
  return { type: EARLY_ACCESS_ENTER_MESSAGE };
}

export type EarlyAccessPlaybackPayload = {
  type: typeof EARLY_ACCESS_PLAYBACK_MESSAGE;
  playing: boolean;
};

export function buildEarlyAccessPlaybackPayload(playing: boolean): EarlyAccessPlaybackPayload {
  return { type: EARLY_ACCESS_PLAYBACK_MESSAGE, playing };
}

export type EarlyAccessReadyPayload = {
  type: typeof EARLY_ACCESS_READY_MESSAGE;
};

export function buildEarlyAccessReadyPayload(): EarlyAccessReadyPayload {
  return { type: EARLY_ACCESS_READY_MESSAGE };
}

// ALLOW-UNKNOWN: iframe postMessage payloads are untyped at runtime.
export function isEarlyAccessReadyPayload(data: unknown): data is EarlyAccessReadyPayload {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const payload = data as EarlyAccessReadyPayload;
  return payload.type === EARLY_ACCESS_READY_MESSAGE;
}

const EMBEDDED_GATE_HALFTONE_SELECTORS = [
  '[data-testid="hero-journey-halftone"]',
  '[data-testid="hero-waitlist-gate-halftone"]',
] as const;

function isElementInsideGateHalftone(element: Element, halftone: HTMLElement): boolean {
  if (halftone.contains(element)) {
    return true;
  }

  // Same-origin iframes: elementsFromPoint can return nodes from the nested document.
  // They are not descendants of the halftone in the parent tree, but the iframe is.
  if (element.ownerDocument !== document) {
    const frame = element.ownerDocument.defaultView?.frameElement;
    return frame instanceof Element && halftone.contains(frame);
  }

  return false;
}

/**
 * Embedded hero gate: enable spotlight over the decorative canvas without
 * stealing clicks from mission/vision copy. The halftone subtree stays
 * pointer-events-none; when the cursor is inside the halftone bounds and only
 * non-interactive layers are under the point, treat it as gate hover.
 */
function findEmbeddedGateHalftoneAt(clientX: number, clientY: number): HTMLElement | null {
  for (const selector of EMBEDDED_GATE_HALFTONE_SELECTORS) {
    const halftone = document.querySelector(selector);
    if (!(halftone instanceof HTMLElement)) {
      continue;
    }

    const rect = halftone.getBoundingClientRect();
    const insideHalftone =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;
    if (insideHalftone) {
      return halftone;
    }
  }

  return null;
}

function isLandingHeaderElement(element: Element): boolean {
  return element.closest('header') !== null;
}

export function isEmbeddedGateSpotlightActive(clientX: number, clientY: number): boolean {
  const halftone = findEmbeddedGateHalftoneAt(clientX, clientY);
  if (halftone === null) {
    return false;
  }

  const stack = document.elementsFromPoint(clientX, clientY);
  for (const element of stack) {
    if (!(element instanceof Element)) {
      continue;
    }
    if (window.getComputedStyle(element).pointerEvents === 'none') {
      continue;
    }
    if (isElementInsideGateHalftone(element, halftone)) {
      return true;
    }
    // Decorative band wrappers (full-bleed host, main, etc.) sit above the
    // pointer-events-none halftone subtree but should not block spotlight.
    if (element.contains(halftone)) {
      continue;
    }
    // Fixed landing header is transparent over the hero band — keep spotlight live.
    if (isLandingHeaderElement(element)) {
      continue;
    }
    return false;
  }

  return true;
}
